import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  EmoneyIssuanceRequestDto,
  TransactionDto,
} from "@/features/wallet/api/dto";

/**
 * Retire de la monnaie électronique (`POST /EmoneyIssuances/redeem`).
 * Strictement symétrique de l'émission. Idempotence obligatoire.
 */
export const useRedeemEmoney = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "emoney", "redeem"],
    mutationFn: (body: EmoneyIssuanceRequestDto) =>
      mutateIdempotent<TransactionDto>({
        scopeId: "wallet.emoney.redeem",
        url: walletEndpoints.emoneyIssuances.redeem,
        body,
      }),
    onSuccess: (data) => {
      return invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(data.id),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useRedeemEmoney failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
